import { useRef } from 'react';

import { Edit02Icon, Link01Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import cn from 'classnames';
import { AnimatePresence, motion } from 'motion/react';
import { useTranslation } from 'react-i18next';

import { Badge } from 'components/Badge/Badge';
import { IconButton } from 'components/IconButton/IconButton';

import { billingAddressLine, billingDetailsStatus } from '../../billing-details';
import { useHighlightFlash } from '../../use-highlight-flash';

const rowVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.3, ease: 'easeOut' } },
};

const BillingDetailsCell = ({ entity, companyEntity }) => {
  const { t } = useTranslation();
  const status = billingDetailsStatus(entity, companyEntity);

  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div
        key={status}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.15 }}>
        {status === 'unsaved' && (
          <span className="text-xl text-text-tertiary">
            {t('company.tabs.billing.table.unsaved')}
          </span>
        )}
        {status === 'missing' && (
          <Badge variant="warning" text={t('company.tabs.billing.table.missing')} />
        )}
        {status === 'company' && (
          <div className="flex flex-col">
            <span className="flex items-center gap-2 text-xl font-medium text-text-secondary">
              <HugeiconsIcon icon={Link01Icon} size={14} className="shrink-0" />
              {t('company.tabs.billing.table.sameAsCompany')}
            </span>
            <span className="text-lg text-text-tertiary">
              {[companyEntity.invoice_entity_name, companyEntity.email].filter(Boolean).join(' · ')}
            </span>
          </div>
        )}
        {status === 'own' && (
          <div className="flex flex-col">
            <span className="text-xl font-medium text-text-primary">
              {entity.invoice_entity_name}
            </span>
            <span className="text-lg text-text-secondary">
              {[entity.email, billingAddressLine(entity)].filter(Boolean).join(' · ')}
            </span>
          </div>
        )}
      </motion.div>
    </AnimatePresence>
  );
};

const BillingEntityRow = ({ row, companyEntity, onEdit, flashToken }) => {
  const { t } = useTranslation();
  const rowRef = useRef(null);
  useHighlightFlash(rowRef, flashToken);

  return (
    <motion.tr
      ref={rowRef}
      variants={rowVariants}
      className={cn('border-t border-border-primary align-middle', {
        'bg-primary-5/[0.05]': !row.entity,
      })}>
      <td className="w-[28%] px-5 py-4 text-xl font-medium text-text-primary">{row.name}</td>
      <td className="px-5 py-4">
        <BillingDetailsCell entity={row.entity} companyEntity={companyEntity} />
      </td>
      <td className="px-5 py-4">
        <div className="flex items-center justify-end gap-2">
          {row.entity && (
            <IconButton
              icon={Edit02Icon}
              prefix="hugeicons"
              tip={t('company.tabs.billing.table.edit')}
              onClick={() => onEdit(row.entity)}
            />
          )}
          {row.extraAction}
        </div>
      </td>
    </motion.tr>
  );
};

export const BillingEntitiesTable = ({
  label,
  rows,
  companyEntity,
  onEdit,
  highlight,
  animateRowsIn = false,
  footer,
}) => {
  const { t } = useTranslation();

  return (
    <div className="overflow-x-auto rounded-xl border border-border-primary bg-white">
      <table className="m-0 w-full min-w-[48rem] border-collapse text-left">
        <thead className="bg-white">
          <tr className="text-lg uppercase tracking-wide text-text-secondary">
            <th className="px-5 py-3 font-medium">{label}</th>
            <th className="px-5 py-3 font-medium">{t('company.tabs.billing.table.billTo')}</th>
            <th className="px-5 py-3 text-right font-medium">
              {t('company.tabs.billing.table.action')}
            </th>
          </tr>
        </thead>
        <motion.tbody
          initial={animateRowsIn ? 'hidden' : false}
          animate="visible"
          variants={{ visible: { transition: { delayChildren: 0.1, staggerChildren: 0.04 } } }}>
          {rows.map(row => (
            <BillingEntityRow
              key={row.key}
              row={row}
              companyEntity={companyEntity}
              onEdit={onEdit}
              flashToken={
                row.entity && highlight?.ids.includes(row.entity.id) ? highlight.token : 0
              }
            />
          ))}
        </motion.tbody>
      </table>
      {footer && <div className="border-t border-border-primary">{footer}</div>}
    </div>
  );
};
